import React from "react";
import { motion } from "framer-motion";
import Tag from "../Tag";
import link from "../../assets/icons8-link-24.png";

const tagAnimateVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: (i) => ({
      delay: 0.05 * i,
    }),
  },
};

/**
 * Renders structured content from JSON data.
 * Supports two content types:
 * - "paragraphs": Array of strings rendered as <p> elements
 * - "list": Array of items with label/text or nested items
 */
function ContentRenderer({ content }) {
  if (!content) return null;

  if (content.type === "paragraphs") {
    return (
      <>
        {content.content.map((paragraph, index) => (
          <p key={index} className="mb-4">
            {paragraph}
          </p>
        ))}
      </>
    );
  }

  if (content.type === "list") {
    return (
      <div className="mb-4">
        <ul className="list-disc pl-4">
          {content.items.map((item, index) => (
            <li key={index}>
              {item.label && (
                <span className="font-normal">{item.label}</span>
              )}
              {item.label && item.text && ": "}
              {item.text}
              {item.items && (
                <ul className="list-disc pl-4">
                  {item.items.map((nestedItem, nestedIndex) => (
                    <li key={nestedIndex}>
                      {nestedItem.label && (
                        <span className="font-normal">{nestedItem.label}</span>
                      )}
                      {nestedItem.label && nestedItem.text && ": "}
                      {nestedItem.text}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return null;
}

function Project({
  image,
  title,
  url,
  type,
  date,
  problem,
  solution,
  management,
  impact,
  contribution,
  tags,
}) {
  return (
    <div className="flex mb-4 p-4 hover:backdrop-opacity-5 hover:backdrop-invert hover:bg-sky/5 text-white  rounded-[3px] duration-300">
      <div>
        <div className=" pb-2 mb-2 flex justify-gap gap-4 text-sky-400">
          <div className="font-normal">{date}</div>
          <div className="flex-1">
            <p className="font-normal flex justify-gap gap-2">
              {title}
              <Url url={url} />
            </p>
            <p className="text-sky-200">{type}</p>
          </div>
        </div>
        <h4 className="font-normal">Problem Identification</h4>
        <section>
          <ContentRenderer content={problem} />
        </section>
        <h4 className="font-normal">Solution Development</h4>
        <section>
          <ContentRenderer content={solution} />
        </section>
        <Management management={management} />
        <Impact impact={impact} />
        <Contribution contribution={contribution} />
        <div className="mt-2 flex flex-wrap">
          {tags.map((tag, i) => (
            <motion.div
              variants={tagAnimateVariants}
              initial="initial"
              whileInView="animate"
              viewport={{
                once: true,
              }}
              custom={i}
              key={i}
            >
              <Tag tag={tag} />
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
function Url({ url }) {
  if (!url) return null;
  return (
    <>
      <a href={url} target="blank" rel="noopener noreferrer">
        <img 
          className="max-h-4 hover:max-h-5 duration-200" 
          src={link} 
          alt="External link" 
        />
      </a>
    </>
  );
}
function Management({ management }) {
  if (!management) return null;
  return (
    <>
      <h4 className="font-normal">Team Management & Methodology</h4>
      <section>
        <ContentRenderer content={management} />
      </section>
    </>
  );
}
function Impact({ impact }) {
  if (!impact) return null;
  return (
    <>
      <h4 className="font-normal">Outcome and Impact</h4>
      <section>
        <ContentRenderer content={impact} />
      </section>
    </>
  );
}
function Contribution({ contribution }) {
  if (!contribution) return null;
  return (
    <>
      <h4 className="font-normal">
        Personal Contribution and Skills Highlight
      </h4>
      <section>
        <ContentRenderer content={contribution} />
      </section>
    </>
  );
}

export default Project;
